import Phaser from 'phaser';

const ZIP_EXTENSION = '.zip';
const PNG_EXTENSION = '.png';

export type SpriteAssetType = 'zip' | 'png' | 'unknown';

export function getSpriteAssetType(filename: string): SpriteAssetType {
  if (filename.endsWith(ZIP_EXTENSION)) return 'zip';
  if (filename.endsWith(PNG_EXTENSION)) return 'png';
  return 'unknown';
}

export function getSpriteUrl(filename: string): string {
  return `/assets/sprites/${filename}`;
}

export function getSpriteAssetKey(filename: string): string {
  const lower = filename.toLowerCase();

  if (lower.endsWith(ZIP_EXTENSION)) {
    return filename.slice(0, -ZIP_EXTENSION.length);
  }

  if (lower.endsWith(PNG_EXTENSION)) {
    return filename.slice(0, -PNG_EXTENSION.length);
  }

  return filename;
}

/**
 * Tears down a sprite's stale Phaser texture and animation registrations,
 * then loads the new version identified by newHash.
 *
 * The caller is responsible for destroying any game objects that use the old
 * texture BEFORE calling this, and for recreating them AFTER it resolves.
 *
 * This is the shared building block for hot-reloading a character sprite in any
 * Phaser scene (MapScene, CombatScene, etc.) without duplicating the teardown logic.
 */
export function reloadSpriteTexture(
  scene: Phaser.Scene,
  filename: string,
): Promise<void> {
  const assetKey = getSpriteAssetKey(filename);

  // Remove all animations in this sprite's namespace. Phaser animations hold
  // direct references to texture Frame objects. Removing the texture makes those
  // refs stale (null), so any fromJSON call during the reload would be skipped
  // (key already exists) and the new sprite would play stale frames. Clearing
  // them first lets the ZIP loader re-register them against the new texture.
  const animPrefix = `${assetKey}:`;
  const staleAnimKeys: string[] = ((scene.anims as any).anims.keys() as string[])
    .filter((k: string) => k.startsWith(animPrefix));
  for (const key of staleAnimKeys) {
    scene.anims.remove(key);
  }

  // Remove the old texture so the Phaser loader doesn't skip it as already cached.
  if (scene.textures.exists(assetKey)) {
    scene.textures.remove(assetKey);
  }

  // Queue the new load.
  const url = getSpriteUrl(filename);
  const type = getSpriteAssetType(filename);
  if (type === 'zip') {
    scene.load.spriteZip(assetKey, url);
  } else if (type === 'png') {
    scene.load.image(assetKey, url);
  } else {
    return Promise.reject(new Error(`Unsupported sprite asset type for: ${filename}`));
  }

  return new Promise<void>((resolve, reject) => {
    scene.load.once('complete', resolve);
    scene.load.once('loaderror', () => reject(new Error(`Failed to load sprite texture: ${filename}`)));
    scene.load.start();
  });
}
